import { useMemo, useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MediaCard from '@/components/base/MediaCard';
import FilterPanel from '@/pages/category/components/FilterPanel';
import { usePageMeta } from '@/hooks/usePageMeta';
import { categoryMeta, filterByType, typeLabel, type FilterState } from '@/utils/media';
import type { MediaType } from '@/mocks/movies';

const PAGE_SIZE = 12;

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [visible, setVisible] = useState(PAGE_SIZE);

  const validType = (['movie', 'tv', 'variety'] as string[]).includes(type ?? '') ? (type as MediaType) : null;

  const filter: FilterState = {
    region: searchParams.get('region') || '全部',
    year: searchParams.get('year') || '全部',
    sort: searchParams.get('sort') || t('sortLatest'),
  };

  const meta = validType ? categoryMeta[validType] : null;

  usePageMeta(
    meta ? `${typeLabel(validType!)} - 久久精品影视资源` : t('notFoundTitle'),
    meta?.desc,
    `久久久精品,${typeLabel(validType ?? 'movie')},影视资源,在线影视资料`,
  );

  const results = useMemo(() => (validType ? filterByType(validType, filter) : []), [validType, filter.region, filter.year, filter.sort]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [filter.region, filter.year, filter.sort, type]);

  const updateFilter = (next: Partial<FilterState>) => {
    const merged = { ...filter, ...next };
    const params: Record<string, string> = {};
    if (merged.region !== '全部') params.region = merged.region;
    if (merged.year !== '全部') params.year = merged.year;
    if (merged.sort !== t('sortLatest')) params.sort = merged.sort;
    setSearchParams(params);
  };

  if (!validType || !meta) {
    return (
      <div className="mx-auto flex max-w-page flex-col items-center px-4 py-24 text-center md:px-6">
        <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">{t('noResultTitle')}</h1>
        <p className="mt-2 text-sm text-foreground-500">{t('noResultDesc')}</p>
        <Link to="/" className="mt-6 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 cursor-pointer">
          {t('backHome')}
        </Link>
      </div>
    );
  }

  const shown = results.slice(0, visible);

  return (
    <div className="jjj-fade-up">
      <div className="relative overflow-hidden bg-primary-950">
        <div className="mx-auto max-w-page px-4 py-10 md:px-6 md:py-14">
          <nav className="mb-3 flex items-center gap-2 text-xs text-background-200/70">
            <Link to="/" className="transition hover:text-accent-300 cursor-pointer">{t('breadcrumbHome')}</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-accent-400">{typeLabel(validType)}</span>
          </nav>
          <h1 className="flex items-center gap-3 font-heading text-3xl font-extrabold text-background-50 md:text-4xl">
            <i className={`${meta.icon} text-accent-400`}></i>
            {meta.title}
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-background-100/80">{meta.desc}</p>
        </div>
      </div>

      <div className="mx-auto max-w-page px-4 py-8 md:px-6 md:py-10">
        <div className="flex flex-col gap-8 lg:flex-row">
          <aside className="hidden w-60 shrink-0 lg:block">
            <div className="sticky top-20 rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="mb-5 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-filter-3-line text-accent-500"></i>
                {t('filterTitle')}
              </h3>
              <FilterPanel filter={filter} onChange={updateFilter} />
            </div>
          </aside>

          <div className="min-w-0 flex-1">
            <div className="mb-5 flex items-center justify-between gap-3">
              <p className="text-sm text-foreground-600">
                {t('resultCountText')} <span className="font-heading font-bold text-accent-600">{results.length}</span> {t('itemCount')}
              </p>
              <button
                type="button"
                onClick={() => setMobileFilterOpen(true)}
                className="flex items-center gap-1.5 rounded-md border border-background-300 px-3.5 py-2 text-sm font-medium text-foreground-700 transition hover:bg-background-100 cursor-pointer lg:hidden"
              >
                <i className="ri-filter-3-line"></i>
                {t('filterTitle')}
              </button>
            </div>

            {results.length === 0 ? (
              <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
                <i className="ri-search-eye-line text-5xl text-foreground-300"></i>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{t('noResultTitle')}</h3>
                <p className="mt-2 text-sm text-foreground-500">{t('noResultDesc')}</p>
                <button
                  type="button"
                  onClick={() => updateFilter({ region: '全部', year: '全部' })}
                  className="mt-6 flex items-center gap-1.5 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition hover:bg-primary-600 cursor-pointer"
                >
                  <i className="ri-refresh-line"></i>
                  {t('resetFilter')}
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 xl:grid-cols-4">
                  {shown.map((item) => (
                    <MediaCard key={item.id} item={item} />
                  ))}
                </div>
                <div className="mt-8 flex flex-col items-center gap-3">
                  {visible < results.length ? (
                    <button
                      type="button"
                      onClick={() => setVisible((v) => v + PAGE_SIZE)}
                      className="flex items-center gap-2 rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-accent-950 transition hover:bg-accent-400 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line text-lg"></i>
                      {t('loadMore')}
                      <span className="text-accent-900/70">({results.length - visible})</span>
                    </button>
                  ) : (
                    <p className="flex items-center gap-2 text-sm text-foreground-400">
                      <i className="ri-checkbox-circle-line"></i>
                      {t('noMore')}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {mobileFilterOpen ? (
        <div className="fixed inset-0 z-[60] flex items-end bg-primary-950/50 lg:hidden" onClick={() => setMobileFilterOpen(false)}>
          <div
            className="max-h-[80vh] w-full overflow-y-auto rounded-t-2xl bg-background-50 p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-5 flex items-center justify-between">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-filter-3-line text-accent-500"></i>
                {t('filterTitle')}
              </h3>
              <button
                type="button"
                onClick={() => setMobileFilterOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 cursor-pointer"
                aria-label="关闭"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <FilterPanel filter={filter} onChange={updateFilter} />
            <button
              type="button"
              onClick={() => setMobileFilterOpen(false)}
              className="mt-6 w-full rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 cursor-pointer"
            >
              查看 {results.length} 部结果
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}